// context/CookieConsentContext.jsx
"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import CookieConsentModal from "@/components/cookies/CookieConsentModal";
import { EEA_COUNTRIES } from "@/lib/geo";
import { readJsonCookie, writeJsonCookie, getCookie } from "@/lib/cookies";

const CookieConsentContext = createContext(null);

const CONFIG = {
    autoAcceptOutsideEEA: true,
    bannerCooldownDays: 90,
    cookieDays: 365,
};

function pushConsentUpdated(next) {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: "consent_updated",
        consent: next,
        ts: Date.now(),
    });
}

function announceConsentUpdated(next) {
    try {
        window.dispatchEvent(
            new CustomEvent("cookieconsent:updated", { detail: next })
        );
    } catch {}
}

export function CookieConsentProvider({ children }) {
    const [consent, setConsent] = useState(null);
    const [show, setShow] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        // Respect cooldown
        const nextBannerAt = readJsonCookie("cookie_consent_banner_next");
        const now = Date.now();
        if (nextBannerAt?.ts && now < nextBannerAt.ts) {
            const c = readJsonCookie("cookie_consent");
            if (c) {
                setConsent(c);
                setShow(false);
                setLoaded(true);
                return;
            }
        }

        // Existing consent
        const existing = readJsonCookie("cookie_consent");
        if (existing) {
            setConsent(existing);
            setShow(false);
            setLoaded(true);
            return;
        }

        // Region logic
        const rawCountry = getCookie("user_country") || "";
        const country = rawCountry.toUpperCase();
        const isEEA = country && EEA_COUNTRIES.includes(country);
        const isUnknown = !rawCountry;

        if (isEEA || isUnknown) {
            setShow(true);
            setLoaded(true);
            return;
        }

        // RoW => accept all by default
        if (CONFIG.autoAcceptOutsideEEA) {
            const acceptedAll = makeConsent({
                analytics: true,
                ads: true,
                personalization: true,
                preferences: true,
                accepted: true,
                region: country || "ZZ",
            });
            writeJsonCookie("cookie_consent", acceptedAll, {
                days: CONFIG.cookieDays,
            });
            writeJsonCookie(
                "cookie_consent_banner_next",
                {
                    ts:
                        Date.now() +
                        CONFIG.bannerCooldownDays * 24 * 60 * 60 * 1000,
                },
                { days: CONFIG.bannerCooldownDays }
            );
            setConsent(acceptedAll);
            setShow(false);
            setLoaded(true);
            pushConsentUpdated(acceptedAll);
            announceConsentUpdated(acceptedAll);
            return;
        }

        // Fallback essential-only (if autoAcceptOutsideEEA is false)
        const baseline = makeConsent({ region: country || "ZZ" });
        writeJsonCookie("cookie_consent", baseline, {
            days: CONFIG.cookieDays,
        });
        setConsent(baseline);
        setShow(false);
        setLoaded(true);
    }, []);

    const acceptAll = () => {
        const region = (getCookie("user_country") || "ZZ").toUpperCase();
        const next = makeConsent({
            analytics: true,
            ads: true,
            personalization: true,
            preferences: true,
            accepted: true,
            region,
        });
        writeJsonCookie("cookie_consent", next, { days: CONFIG.cookieDays });
        writeJsonCookie(
            "cookie_consent_banner_next",
            {
                ts:
                    Date.now() +
                    CONFIG.bannerCooldownDays * 24 * 60 * 60 * 1000,
            },
            { days: CONFIG.bannerCooldownDays }
        );
        setConsent(next);
        setShow(false);
        pushConsentUpdated(next);
        announceConsentUpdated(next);
    };

    const saveChoices = (choices) => {
        const region = (getCookie("user_country") || "ZZ").toUpperCase();
        const next = makeConsent({
            ...choices,
            accepted: !!(
                choices.analytics ||
                choices.ads ||
                choices.personalization ||
                choices.preferences
            ),
            region,
        });
        writeJsonCookie("cookie_consent", next, { days: CONFIG.cookieDays });
        writeJsonCookie(
            "cookie_consent_banner_next",
            {
                ts:
                    Date.now() +
                    CONFIG.bannerCooldownDays * 24 * 60 * 60 * 1000,
            },
            { days: CONFIG.bannerCooldownDays }
        );
        setConsent(next);
        setShow(false);
        pushConsentUpdated(next);
        announceConsentUpdated(next);
    };

    const rejectAll = () => {
        const region = (getCookie("user_country") || "ZZ").toUpperCase();
        const next = makeConsent({
            analytics: false,
            ads: false,
            personalization: false,
            preferences: false,
            accepted: false,
            region,
        });
        writeJsonCookie("cookie_consent", next, { days: CONFIG.cookieDays });
        writeJsonCookie(
            "cookie_consent_banner_next",
            {
                ts:
                    Date.now() +
                    CONFIG.bannerCooldownDays * 24 * 60 * 60 * 1000,
            },
            { days: CONFIG.bannerCooldownDays }
        );
        setConsent(next);
        setShow(false);
        pushConsentUpdated(next);
        announceConsentUpdated(next);
    };

    const value = useMemo(
        () => ({
            consent,
            showBanner: show,
            acceptAll,
            rejectAll,
            saveChoices,
            // keep openManager for manual use from a /privacy page if you want
            openManager: () => setShow(true),
            closeManager: () => setShow(false),
        }),
        [consent, show]
    );

    const remindMeLater = () => {
        // snooze the banner without changing consent
        writeJsonCookie(
            "cookie_consent_banner_next",
            {
                ts:
                    Date.now() +
                    CONFIG.bannerCooldownDays * 24 * 60 * 60 * 1000,
            },
            { days: CONFIG.bannerCooldownDays }
        );
        setShow(false);
    };

    const hasExistingConsent = !!consent;

    return (
        <CookieConsentContext.Provider value={value}>
            {children}

            {loaded && show && (
                <CookieConsentModal
                    defaultValues={{
                        analytics: !!consent?.analytics,
                        personalization: !!consent?.personalization,
                        ads: !!consent?.ads,
                        preferences: !!consent?.preferences,
                    }}
                    showRemindLater={hasExistingConsent} // NEW
                    onAcceptAll={acceptAll}
                    onRejectAll={rejectAll}
                    onSave={saveChoices}
                    onRemindLater={remindMeLater} // <— NEW
                    onClose={() => setShow(false)}
                    privacyHref="/privacy-policy"
                    termsHref="/terms"
                />
            )}
        </CookieConsentContext.Provider>
    );
}

export function useCookieConsent() {
    return useContext(CookieConsentContext);
}

function makeConsent({
    analytics = false,
    ads = false,
    personalization = false,
    preferences = false,
    accepted = false,
    region = "ZZ",
} = {}) {
    return {
        essential: true,
        analytics,
        ads,
        personalization,
        preferences,
        accepted,
        updatedAt: new Date().toISOString(),
        region,
    };
}
